"use client";

import rippleEffect from "@/utils/rippleEffect";
import Image from "next/image";
import Link from "next/link";
import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./style.module.scss";
import { convertToDecimal } from "@/utils/convertToDecimal";
import Decimal from "decimal.js";
import { CryptoCurrency } from "@/service/queries/cryptoCurrencies";

const Calculator: FC<{ data: CryptoCurrency; coin: string }> = ({
  data,
  coin,
}) => {
  const [bottomSheetOpen, toggleBottomSheetOpen] = useState<boolean>(false);
  const [calculatorUnit, setCalculatorUnit] = useState<"TMN" | "USDT">("TMN");
  const [calculatorOutput, setCalculatorOutput] = useState<Decimal>();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== "") {
      if (calculatorUnit === "TMN")
        setCalculatorOutput(
          convertToDecimal(inputRef.current.value).dividedBy(
            Number(data?.price_in_irr) / 10,
          ),
        );
      else if (calculatorUnit === "USDT")
        setCalculatorOutput(
          convertToDecimal(inputRef.current.value).dividedBy(
            Number(data?.price_in_usdt),
          ),
        );
    }
  }, [calculatorUnit, data?.price_in_irr, data?.price_in_usdt]);

  const inputKeyDownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    if (
      !(
        e.key === "0" ||
        e.key === "1" ||
        e.key === "2" ||
        e.key === "3" ||
        e.key === "4" ||
        e.key === "5" ||
        e.key === "6" ||
        e.key === "7" ||
        e.key === "8" ||
        e.key === "9" ||
        e.key === "Backspace" ||
        e.key === "Delete" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "." ||
        (e.code === "KeyA" && e.ctrlKey)
      )
    )
      e.preventDefault();

    if (e.key === "." && e.currentTarget.value.split(".").length === 2)
      e.preventDefault();

    if (
      (e.key === "0" ||
        e.key === "1" ||
        e.key === "2" ||
        e.key === "3" ||
        e.key === "4" ||
        e.key === "5" ||
        e.key === "6" ||
        e.key === "7" ||
        e.key === "8" ||
        e.key === "9") &&
      e.currentTarget.value.split(".").length === 2 &&
      e.currentTarget.value.split(".")[1].length > 2
    )
      e.preventDefault();
  };

  const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 0 && !isNaN(e.target.value as any)) {
      if (calculatorUnit === "TMN")
        setCalculatorOutput(
          convertToDecimal(e.target.value).dividedBy(
            Number(data?.price_in_irr) / 10,
          ),
        );
      else if (calculatorUnit === "USDT")
        setCalculatorOutput(
          convertToDecimal(e.target.value).dividedBy(
            Number(data?.price_in_usdt),
          ),
        );
    } else setCalculatorOutput(undefined);
  };

  return (
    <div
      className={`${styles.calculator}${
        bottomSheetOpen ? " " + styles.open : ""
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* <button
        onClick={() => toggleBottomSheetOpen(true)}
        onMouseDown={rippleEffect}
        onTouchStart={rippleEffect}
        name="toggleBottomSheetOpen"
      >
        <BitcoinConvert />
        محاسبه قیمت
      </button> */}

      <div className={styles.title}>
        <em>محاسبه قیمت</em>
        <button
          className="fi-rr-cross-small"
          onClick={() => toggleBottomSheetOpen(false)}
        ></button>
      </div>

      <div className={styles.input}>
        <input
          type="tel"
          inputMode="tel"
          required
          maxLength={14}
          onKeyDown={inputKeyDownHandler}
          onChange={inputChangeHandler}
          ref={inputRef}
        />
        <label>{`مقدار ${
          calculatorUnit === "TMN" ? "تومان" : "تتر"
        } را وارد کنید`}</label>
        <button
          className="fi-rr-angle-small-up"
          onClick={(e) => {
            if (!e.currentTarget.nextElementSibling?.hasAttribute("class"))
              e.currentTarget.nextElementSibling?.classList.add(styles.show);
            else e.currentTarget.nextElementSibling?.removeAttribute("class");
          }}
        >
          <Image
            src="/logos/tmn.svg"
            hidden={calculatorUnit === "USDT"}
            alt="لوگوی تومان"
            loading="lazy"
            width={24}
            height={24}
          />
          <Image
            src="/logos/usdt.svg"
            hidden={calculatorUnit === "TMN"}
            alt="لوگوی تتر"
            loading="lazy"
            width={24}
            height={24}
          />
          {calculatorUnit === "TMN" ? "تومان" : "تتر"}
        </button>

        <ul>
          <li>
            <button
              onClick={(e) => {
                setCalculatorUnit("TMN");
                e.currentTarget.parentElement?.parentElement?.removeAttribute(
                  "class",
                );
              }}
            >
              <span>TMN</span>
              <span>تومان</span>
              <Image
                src="/logos/tmn.svg"
                alt="لوگوی تومان"
                loading="lazy"
                width={30}
                height={30}
              />
            </button>
          </li>
          {data?.crypto_currency_name !== "USDT" && (
            <li>
              <button
                onClick={(e) => {
                  setCalculatorUnit("USDT");
                  e.currentTarget.parentElement?.parentElement?.removeAttribute(
                    "class",
                  );
                }}
              >
                <span>USDT</span>
                <span>تتر</span>
                <Image
                  src="/logos/usdt.svg"
                  alt="لوگوی تتر"
                  loading="lazy"
                  width={30}
                  height={30}
                />
              </button>
            </li>
          )}
        </ul>
      </div>

      <div className={styles.output}>
        <output>{calculatorOutput?.toFixed(6)}</output>
        <label>مقدار {data?.persian_name}</label>
      </div>

      <div className={styles.buttonGroup}>
        <Link
          href={`${process.env.APP_PANEL_URL}/trade/${
            data?.crypto_currency_name === "USDT" ? "USDT_TMN" : coin + "_USDT"
          }`}
          onMouseDown={rippleEffect}
          onTouchStart={rippleEffect}
          draggable={false}
        >
          فروش
        </Link>
        <Link
          href={`${process.env.APP_PANEL_URL}/trade/${
            data?.crypto_currency_name === "USDT" ? "USDT_TMN" : coin + "_USDT"
          }`}
          onMouseDown={rippleEffect}
          onTouchStart={rippleEffect}
          draggable={false}
        >
          خرید
        </Link>
      </div>
    </div>
  );
};

export default Calculator;
